import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import type { Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import type { IGender } from '../Interfaces/IGender';
import type { ILegalClientFullObject } from '../Interfaces/ILegalClient';
import type { State } from '../redux/mainReducer';

interface IProps {
  formValues: ILegalClientFullObject;
  setFormValues: Dispatch<SetStateAction<ILegalClientFullObject>>;
}

export const NaturalPersonForm = ({ formValues, setFormValues }: IProps) => {
  const genders = useSelector((state: State) => state.genderArray.storeGender);
  const academicTitles = useSelector(
    (state: State) => state.academicTitleArray.storeAcademicTitle
  );
  const contactGroups = useSelector(
    (state: State) => state.contactGroup.storeContactGroup
  );

  const countries = useSelector(
    (state: State) => state.countryArray.storeCountry
  );

  return (
    <>
      <Grid item xs={3}>
        <FormControl fullWidth>
          <InputLabel>Gruppe</InputLabel>
          <Select
            label='Gruppe'
            size='small'
            fullWidth
            value={formValues?.idContactGroup ?? 0}
            onChange={({ target: { value } }) => {
              const contactGroupId = Number(value) || null;
              const group = contactGroups.find(
                cg => cg.idContactGroup === contactGroupId
              )?.ContactGroup;

              setFormValues((prev: ILegalClientFullObject) => ({
                ...prev,
                idContactGroup: contactGroupId,
                ContactGroup: group || null,
              }));
            }}
          >
            <MenuItem key={0} value={0} defaultChecked>
              <i>Keine Gruppe zugeordnet</i>
            </MenuItem>
            {contactGroups.map((cg, i) => (
              <MenuItem key={i + 1} value={cg.idContactGroup}>
                {cg.ContactGroup}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6} display='flex' justifyContent='flex-end'>
        {formValues.idArnoTopIntern && (
          <Box>
            <strong>Namen.Nr.:</strong> {formValues.idArnoTopIntern}
          </Box>
        )}
      </Grid>
      <Grid item xs={3}>
        <TextField
          select
          required
          value={formValues.idGender || ''}
          label='Geschlecht'
          size='small'
          fullWidth
          onChange={event => {
            const genderId = Number(event.target.value);

            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              idGender: genderId,
              Gender: genders.find(gender => gender.idGender === genderId)
                ?.Gender,
            }));
          }}
        >
          {genders.map((gender: IGender, i) => (
            <MenuItem key={i} value={gender.idGender}>
              {gender.Gender}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={3}>
        <TextField
          select
          label='Akademischer Titel'
          size='small'
          fullWidth
          value={formValues.idAcademicTitle || ''}
          onChange={event => {
            const academicTitleId = Number(event.target.value);
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              idAcademicTitle: academicTitleId,
              AcademicTitle: academicTitles.find(
                title => title.idAcademicTitle === academicTitleId
              )?.AcademicTitle,
            }));
          }}
        >
          {academicTitles.map((title, i) => (
            <MenuItem key={i} value={title.idAcademicTitle}>
              {title.AcademicTitle}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={3}>
        <TextField
          required
          value={formValues.FirstName || ''}
          label='Vorname'
          size='small'
          fullWidth
          onChange={event => {
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              FirstName: event.target.value,
            }));
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          required
          value={formValues.LastName || ''}
          label='Nachname'
          size='small'
          fullWidth
          onChange={event => {
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              LastName: event.target.value,
            }));
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ mt: 0.5 }} />
      </Grid>
      <Grid item xs={4}>
        <TextField
          InputLabelProps={{ shrink: true }}
          label='Geburtstag'
          size='small'
          value={formValues.Birthdate || ''}
          fullWidth
          type='date'
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              Birthdate: event.target.value,
            }))
          }
        />
      </Grid>

      <Grid item xs={4}>
        <TextField
          label='Geburtsort'
          size='small'
          value={formValues.Birthplace || ''}
          fullWidth
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              Birthplace: event.target.value,
            }))
          }
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          label='Nationalität'
          size='small'
          value={formValues.idCountry || ''}
          fullWidth
          select
          onChange={event =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              idCountry: Number(event.target.value),
            }))
          }
        >
          {countries.map((c, i) => (
            <MenuItem key={i} value={c.idCountry}>
              {c.Nationality}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={6}>
        <TextField
          InputLabelProps={{ shrink: true }}
          label='Sterbedatum'
          size='small'
          value={formValues.DateOfDeath || ''}
          fullWidth
          type='date'
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              DateOfDeath: event.target.value,
            }))
          }
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          label='Sterbeort'
          size='small'
          value={formValues.PlaceOfDeath || ''}
          fullWidth
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              PlaceOfDeath: event.target.value,
            }))
          }
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ mt: 0.5 }} />
      </Grid>

      <Grid item xs={3}>
        <TextField
          label='Steuerliche Identifikationsnummer'
          size='small'
          value={formValues.TaxNumber || ''}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              TaxNumber: event.target.value,
            }))
          }
          fullWidth
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ mt: 0.5 }} />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label='E-Mail-Adresse'
          size='small'
          value={formValues.Email || ''}
          fullWidth
          type='email'
          onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              Email: target.value,

              ...(!target.value
                ? {
                    passwordResetRequired: false,
                    resetPassword: false,
                    isReceivingEmailEnabled: false,
                    loginPossible: false,
                  }
                : formValues.idLegalClient < 0 && {
                    passwordResetRequired: true,
                    resetPassword: false,
                    isReceivingEmailEnabled: false,
                    loginPossible: true,
                  }),
            }))
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ mt: 0.5 }} />
      </Grid>
    </>
  );
};
